/**
 * Types for the Precedent upload system on the Enrichir page.
 * Handles batch upload of contract documents with hash-based deduplication.
 */

/**
 * Upload status for a precedent file.
 * Represents the full lifecycle from selection to completion.
 */
export type PrecedentUploadStatus =
  | 'pending'      // Waiting to be processed
  | 'hashing'      // Computing SHA-256 hash
  | 'uploading'    // Upload to S3 in progress
  | 'processing'   // Backend extraction in progress
  | 'analyzed'     // Successfully completed
  | 'error';       // Error occurred

/**
 * File object for the Enrichir page upload queue.
 * Tracks file metadata, upload state, and backend processing status.
 */
export interface EnrichFile {
  /** Local UUID for tracking in the queue */
  id: string;

  /** The actual File object */
  file: File;

  /** Filename only (e.g., "contrat.pdf") */
  name: string;

  /**
   * Relative path including folder hierarchy when uploaded via folder selection.
   * Example: "Clients/Acme/2024/contrat.pdf"
   * Null/undefined if uploaded as individual file.
   */
  relativePath?: string;

  /** File size in bytes */
  size: number;

  /** File type */
  type: 'pdf' | 'docx';

  // === Upload state ===

  /** SHA-256 hash of file contents (computed client-side) */
  hash?: string;

  /** Current upload/processing status */
  status: PrecedentUploadStatus;

  /** Upload progress 0-100 (only during 'uploading' status) */
  uploadProgress?: number;

  /** Error message if status is 'error' */
  errorMessage?: string;

  // === Backend state ===

  /** Backend precedent ID (set after confirm-upload) */
  precedentId?: number;

  /** S3 storage key (set after init-upload) */
  storageKey?: string;

  /** True if file already exists (same hash found for company) */
  isDuplicate?: boolean;

  /** Existing precedent info when isDuplicate is true */
  existingPrecedent?: {
    id: number;
    filename: string;
    relativePath?: string;
    status: string;
    createdAt: string;
  };
}

/**
 * Status labels for display in the UI.
 */
export const PRECEDENT_STATUS_LABELS: Record<PrecedentUploadStatus, string> = {
  pending: 'En attente',
  hashing: 'Préparation...',
  uploading: 'Envoi',
  processing: 'Analyse...',
  analyzed: 'Terminé',
  error: 'Erreur',
};
